import React from 'react';
import { FileText, GitCompare, Check, ArrowRight } from 'lucide-react';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * 🎯 EVALUATION MODE SELECTOR - Flashcard Style
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Allows user to choose between:
 * - Single Brand: Detailed evaluation of one name
 * - Compare Brands: Side-by-side comparison of 2-5 names with winner recommendation
 */

const EvaluationModeSelector = ({ selectedMode, onSelectMode }) => {
  const modes = [
    {
      id: 'single',
      title: 'Single Brand',
      subtitle: 'Deep Analysis',
      icon: FileText,
      description: 'Evaluate one brand name with comprehensive analysis',
      features: [
        'Detailed trademark research',
        'Full linguistic analysis',
        'Complete competitive intelligence',
        'In-depth cultural analysis',
        'Legal risk assessment'
      ],
      recommended: false,
      color: 'violet'
    },
    {
      id: 'compare',
      title: 'Compare Brands',
      subtitle: 'Find the Winner',
      icon: GitCompare,
      description: 'Compare 2-5 brand names and get a recommendation',
      features: [
        'Side-by-side comparison',
        'Winner recommendation',
        'Risk comparison matrix',
        'Availability comparison',
        'Best choice insights'
      ],
      recommended: true,
      color: 'emerald'
    }
  ];

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-slate-800 mb-2">
          How would you like to evaluate?
        </h2>
        <p className="text-slate-600">
          Choose single for deep analysis or compare to find the best name
        </p>
      </div>

      {/* Flashcards */}
      <div className="grid md:grid-cols-2 gap-6">
        {modes.map((mode) => {
          const Icon = mode.icon;
          const isSelected = selectedMode === mode.id;
          
          return (
            <div
              key={mode.id}
              onClick={() => onSelectMode(mode.id)}
              className={`
                relative cursor-pointer rounded-2xl border-2 p-6 transition-all duration-300
                ${isSelected 
                  ? mode.color === 'violet'
                    ? 'border-violet-500 bg-violet-50 shadow-lg shadow-violet-100 scale-[1.02]'
                    : 'border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-100 scale-[1.02]'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md'
                }
              `}
            >
              {/* Recommended Badge */}
              {mode.recommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-emerald-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    RECOMMENDED
                  </span>
                </div>
              )}

              {/* Selected Indicator */}
              {isSelected && (
                <div className={`absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center
                  ${mode.color === 'violet' ? 'bg-violet-500' : 'bg-emerald-500'}
                `}>
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}

              {/* Icon */}
              <div className={`
                w-14 h-14 rounded-xl flex items-center justify-center mb-4
                ${isSelected
                  ? mode.color === 'violet' ? 'bg-violet-500' : 'bg-emerald-500'
                  : 'bg-slate-100'
                }
              `}>
                <Icon className={`w-7 h-7 ${isSelected ? 'text-white' : 'text-slate-600'}`} />
              </div>

              {/* Title & Subtitle */}
              <div className="mb-3">
                <h3 className={`text-xl font-bold ${isSelected 
                  ? mode.color === 'violet' ? 'text-violet-700' : 'text-emerald-700'
                  : 'text-slate-800'
                }`}>
                  {mode.title}
                </h3>
                <p className={`text-sm font-medium ${isSelected
                  ? mode.color === 'violet' ? 'text-violet-500' : 'text-emerald-500'
                  : 'text-slate-500'
                }`}>
                  {mode.subtitle}
                </p>
              </div>

              {/* Description */}
              <p className="text-slate-600 text-sm mb-4">
                {mode.description}
              </p>

              {/* Features */}
              <ul className="space-y-2">
                {mode.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className={`w-4 h-4 ${isSelected
                      ? mode.color === 'violet' ? 'text-violet-500' : 'text-emerald-500'
                      : 'text-slate-400'
                    }`} />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Select Button */}
              <button
                className={`
                  w-full mt-6 py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all
                  ${isSelected
                    ? mode.color === 'violet'
                      ? 'bg-violet-500 text-white hover:bg-violet-600'
                      : 'bg-emerald-500 text-white hover:bg-emerald-600'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }
                `}
              >
                {isSelected ? 'Selected' : 'Select'}
                {isSelected && <Check className="w-4 h-4" />}
                {!isSelected && <ArrowRight className="w-4 h-4" />}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EvaluationModeSelector;
